<template>
    <div class="display-contents">
        <div class="col-sm-12 u-mb-small">
            <div class="c-field">
                <vx-form-group :hint="`Paste or press enter to add up to ${restrictions.MAX_TAGS_PER_CONTACT} tags separated by comma.`">
                    <vx-input
                        type="text"
                        placeholder="Please enter to add"
                        :disabled="readOnly"
                        v-model="newTag"
                        @paste="onPaste"
                        @keydown.enter="onEnterInput"
                        />
                </vx-form-group>

                <tag-search v-if="newTag" @selected="onSelected" :canCreate="true" @create="onCreate" :search="newTag"></tag-search>
            </div>
            <div class="mb-3">
                <v-tooltip
                        v-for="tag in selectedTags" :key="tag.tag_name"
                    >
                        <vx-badge
                            class="mr-2 mb-1"
                            :color="tag.invalid ? 'tertiary' : 'primary'"
                            :close="true"
                            @close="removeTag(tag.tag_name)"
                        >
                            {{ tag.tag_name }}
                        </vx-badge>
                        <template v-slot:content v-if="tag.invalid">
                            {{ tag.invalid }}
                        </template>
                    </v-tooltip>
            </div>
        </div>
    </div>
</template>

<script setup>
import { restrictions } from '../../constants/tags';
import { validate } from '../../utils/tags';
import { VxFormGroup, VxInput, VxBadge } from '@voxie/frontend-components-v3';
import VTooltip from './VTooltip.vue';
import TagSearch from './TagSearch.vue';
import { ref } from 'vue';
import { uniqBy } from '~/utils/helpers';
import { useToasted } from '../../composables/useToasted';

const $toasted = useToasted();
const emit = defineEmits(['add', 'remove']);
const props = defineProps({
    selectedTags: {
        type: Array,
        default: () => []
    },
    readOnly: {
        default: false,
        type: Boolean,
    },
});

const newTag = ref(null);

const onSelected = (tag) => {
    if (!validate(tag)) {
        return;
    }

    if (props.selectedTags.length + 1 > restrictions.MAX_TAGS_PER_CONTACT) {
        tagsLimitReachedAlert();
        return;
    }

    emit('add', {
        id: tag.id,
        tag_name: tag.tag_name,
    });

    newTag.value = null;
};

const mapTags = (input)=> {
    if (!input || input?.trim().length <= 0) {
       return [];
    }

    return uniqBy(input.split(/\r?\n|\r|\n|,/g).map((tagName) => {
        if (!tagName?.trim()?.length) {
            return null;
        }

        return {
            tag_name: tagName.trim().toLowerCase(),
        }
    }).filter((tag) => tag !== null), 'tag_name');
}

const onEnterInput = (event) => {
    event.preventDefault();

    let newTags = mapTags(newTag.value);
    let tagsAreValid = newTags.length > 0;
    for (let tag of newTags) {
        if (!validate(tag)) {
            tagsAreValid = false;
            break;
        }
    }

    if (!tagsAreValid) {
        return;
    }

    if (props.selectedTags.length + newTags.length > restrictions.MAX_TAGS_PER_CONTACT) {
        tagsLimitReachedAlert();
        const tagsToSlice = newTags.length - (newTags.length - restrictions.MAX_TAGS_PER_CONTACT) - props.selectedTags.length;
        newTags = newTags.slice(0, tagsToSlice);
    }

    processTags(newTags);
};

const onPaste = (event) => {
    event.preventDefault();

    const text = event.clipboardData.getData('text');

    let newTags = mapTags(text);
    let tagsAreValid = newTags.length > 0;

    for (let tag of newTags) {
        if (!validate(tag)) {
            tagsAreValid = false;
            break;
        }
    }

    if (!tagsAreValid) {
        return;
    }

    if (props.selectedTags.length + newTags.length > restrictions.MAX_TAGS_PER_CONTACT) {
        tagsLimitReachedAlert();
        const tagsToSlice = newTags.length - (newTags.length - restrictions.MAX_TAGS_PER_CONTACT) - props.selectedTags.length;
        newTags = newTags.slice(0, tagsToSlice);
    }

    processTags(newTags);
};

const onCreate = (input) => {
    let newTags = mapTags(input);
    let tagsAreValid = newTags.length > 0;
    for (let tag of newTags) {
        if (!validate(tag)) {
            tagsAreValid = false;
            break;
        }
    }

    if (!tagsAreValid) {
        return;
    }

    if (props.selectedTags.length + newTags.length > restrictions.MAX_TAGS_PER_CONTACT) {
        tagsLimitReachedAlert();
        const tagsToSlice = newTags.length - (newTags.length - restrictions.MAX_TAGS_PER_CONTACT) - props.selectedTags.length;
        newTags = newTags.slice(0, tagsToSlice);
    }

    processTags(newTags);
};

const processTags = (tags) => {
    tags.forEach((tag) => {
        emit('add', {
            tag_name: tag.tag_name.toLowerCase(),
        });
    });

    newTag.value = null;
}

const removeTag = (tagName) => {
    emit('remove', tagName)
}

const limitReachedMessage = `A contact cannot have more than ${restrictions.MAX_TAGS_PER_CONTACT} tags.`;
const tagsLimitReachedAlert = () => {
    $toasted.show(limitReachedMessage);
};
</script>

<style scoped>
.display-contents {
    display: contents;
}
</style>
